import { inject, Injectable } from "@angular/core";
import {
  Course,
  CourseOrTestWithPlanStatus,
  CourseOrTestWithProgressStatus,
  CourseWithLabelColor,
  PossibleCustomCourse,
} from "@core/domain/course";
import { CourseDetail } from "@core/domain/course-detail";
import { PlanHelper } from "@core/domain/helpers/plan-helper";
import { PlanDetails } from "@core/domain/plan-details";
import { AuthService } from "@core/services/auth.service";
import { TranslateService } from "@ngx-translate/core";
import { CourseTestBlock } from "../course-test";
import { ProposalBlocksExamComponents } from "../proposal-status";

@Injectable({
  providedIn: "root",
})
export class CourseHelper {
  private readonly colorRegex = /#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})/;

  private readonly authService = inject(AuthService);
  private readonly planHelper = inject(PlanHelper);
  private readonly translate = inject(TranslateService);

  getStatusObtainedLabel(courseOrTest: CourseOrTestWithProgressStatus): string {
    if (courseOrTest.statusObtained === undefined) {
      return "";
    }
    return courseOrTest.statusObtained ? "planCourse.statusObtained" : "planCourse.statusNotObtained";
  }

  isEnrolledInPlanPeriod(courseOrTest: CourseOrTestWithPlanStatus): boolean {
    return !!courseOrTest.enrolledPeriods?.some(
      (enrolledPeriod) =>
        enrolledPeriod.yearNr === courseOrTest.yearNr && enrolledPeriod.periodNr === courseOrTest.periodNr,
    );
  }

  showEnrolled(courseOrTest: CourseOrTestWithPlanStatus): boolean {
    if (courseOrTest.isTest) {
      return courseOrTest.isEnrolled;
    }
    // if it is not a test, it is a course and we need to check ribbon course rules
    const course = courseOrTest as Course;
    return course.isEnrolled && (!this.isRibbonCourse(course) || this.isFirstPartOfRibbon(course));
  }

  showEnrolledInOtherPeriod(courseOrTest: CourseOrTestWithPlanStatus): boolean {
    return (
      this.showEnrolled(courseOrTest) &&
      !!courseOrTest.enrolledPeriods?.some(
        (enrolledPeriod) =>
          enrolledPeriod.yearNr !== courseOrTest.yearNr || enrolledPeriod.periodNr !== courseOrTest.periodNr,
      )
    );
  }

  getEnrolledInOtherPeriodDescription(courseOrTest: CourseOrTestWithPlanStatus, plan: PlanDetails): string[] {
    if (!this.showEnrolledInOtherPeriod(courseOrTest)) {
      return [];
    }

    const otherEnrolledPeriods =
      courseOrTest.enrolledPeriods?.filter(
        (enrolledPeriod) =>
          enrolledPeriod.yearNr !== courseOrTest.yearNr || enrolledPeriod.periodNr !== courseOrTest.periodNr,
      ) ?? [];
    if (!otherEnrolledPeriods.length) {
      return [];
    }

    const yearLabel = this.translate.instant("course.year") as string;
    return otherEnrolledPeriods
      .map((otherEnrolledPeriod) => {
        // from the plan, get the studyYear that corresponds to the enrolled period
        const studyYear = plan?.studyYears.find((year) => year.yearNr === otherEnrolledPeriod.yearNr);
        if (!studyYear) {
          return "";
        }
        // get the period that corresponds to the enrolled period
        const period = studyYear.periods.find((thisPeriod) => thisPeriod.periodNr === otherEnrolledPeriod.periodNr);
        if (!period) {
          return "";
        }
        return `in ${yearLabel} ${otherEnrolledPeriod.yearNr} - ${period.periodDescription}`;
      })
      .filter((description) => description !== "");
  }

  isCourseObtained(course: CourseOrTestWithProgressStatus): boolean {
    return course?.statusObtained === true;
  }

  public isCustomCourse(course: PossibleCustomCourse): boolean {
    return !!course.customCourseName?.length;
  }

  getCourseId(course: Course): string {
    const courseIdOrCode = course.id ?? course.code;
    // Return the course id, optionally with ribbon number if it is a ribbon course
    return course.ribbonNr === 1 ? `${courseIdOrCode}` : `${courseIdOrCode}-${course.ribbonNr}`;
  }

  getCourseName(course: PossibleCustomCourse): string {
    return this.isCustomCourse(course) ? course.customCourseName : course.name!;
  }

  hasRibbonPeriods(course: Course): boolean {
    return !!course.ribbonPeriods?.length;
  }

  isRibbonCourse(course: Course): boolean {
    // This function is used to see if the link icon may be displayed
    return this.hasRibbonPeriods(course) && course.yearNr > 0;
  }

  isFirstPartOfRibbon(course: Course): boolean {
    return !!course.ribbonPeriods?.length && course.ribbonNr === 1;
  }

  isMoveCourseAllowed(course: Course): boolean {
    return (
      course.statusObtained !== true &&
      !course.isRibbonRepeat &&
      !course.isCourseAtAnotherInstitution &&
      !this.isCustomCourse(course) &&
      (this.authService.isRoleStudent() || this.authService.isRoleEmployeeDraftPlans())
    );
  }

  isMoveTestAllowed(courseOrTestBlock: Course | CourseTestBlock, plan?: PlanDetails): boolean {
    return (
      !!plan?.allowsTestPlanning &&
      !!courseOrTestBlock.courseTests?.length &&
      (this.authService.isRoleStudent() || this.authService.isRoleEmployeeDraftPlans())
    );
  }

  private courseIsInLockedProposal(course: Course, plan?: PlanDetails): boolean {
    if (!plan) {
      // a plan is not passed, so the course is not in a locked proposal
      return false;
    }
    return this.planHelper.proposalStatusIsLocked(course.proposalStatus, plan.proposalReviewMoment);
  }

  private courseMustStayInPlan(course: Course, plan?: PlanDetails): boolean {
    return course.isEnrolled || !!course.statusObtained || this.courseIsInLockedProposal(course, plan);
  }

  isRemoveCourseAllowed(course: Course, plan?: PlanDetails): boolean {
    return (
      course.ribbonNr === 1 &&
      !course.isCourseAtAnotherInstitution &&
      (this.authService.isRoleStudent() || this.authService.isRoleEmployeeDraftPlans()) &&
      !this.courseMustStayInPlan(course, plan)
    );
  }

  isShowCourseInfoAllowed(course: Course): boolean {
    return !this.isCustomCourse(course) && !course.isCourseAtAnotherInstitution;
  }

  isShowTestInfoAllowed(course: Course, plan?: PlanDetails): boolean {
    // verwijder als oude toetsinschrijvingen verwijderd worden
    return !plan?.allowsTestPlanning && !!course.courseTests?.length;
  }

  private courseIsInObtainedStudyProgram(course: Course, plan: PlanDetails): boolean {
    return (
      (course.studyProgram === plan.studyProgram && plan.studyProgramObtained) ||
      (course.studyProgram === plan.secondStudyProgram && plan.secondStudyProgramObtained)
    );
  }

  isChangeExamComponentAllowed(course: Course, plan?: PlanDetails): boolean {
    return (
      !!plan?.allowsChangeExamComponent &&
      !course.isInRequiredExamComponent &&
      !course.isRibbonRepeat &&
      !this.courseIsInLockedProposal(course, plan) &&
      plan.proposalBlocksExamComponents !== ProposalBlocksExamComponents.ALL &&
      !this.courseIsInObtainedStudyProgram(course, plan) &&
      (this.authService.isRoleStudent() || this.authService.isRoleEmployeeDraftPlans())
    );
  }

  hasActions(course: Course, plan?: PlanDetails): boolean {
    if (course.isCourseAtAnotherInstitution) {
      // for courses at another institution the only possible action is change exam component
      return this.isChangeExamComponentAllowed(course, plan);
    }
    if (this.isCustomCourse(course)) {
      // for custom courses the only possible actions are remove and change exam component
      return this.isRemoveCourseAllowed(course, plan) || this.isChangeExamComponentAllowed(course, plan);
    }
    // for other courses 'show info' is always present, so it has actions
    return true;
  }

  /**
   * Returns the color of the course if it is a valid color, otherwise undefined.
   */
  getLabelColor(course: CourseWithLabelColor): string | undefined {
    return course.labelColor && this.colorRegex.test(course.labelColor) ? course.labelColor : undefined;
  }

  createEmptyCourse(): Course {
    return {
      studyPoints: 0,
      customCourseName: "",
      customCoursePoints: 0,
      timeslots: [],
      ribbonNr: 1,
      isInPlanLater: false,
      isFlexCourse: false,
      isCourseAtAnotherInstitution: false,
    } as unknown as Course;
  }

  isSameCourse(course1: PossibleCustomCourse, course2: Course): boolean {
    // if one of the courses is undefined, they are not the same
    if (!course1 || !course2) {
      return false;
    }

    if (!!course1.code && !!course2.code) {
      // compare by code if both have a code
      return course1.code === course2.code;
    }

    if (!!course1.id && !!course2.id) {
      // compare by id if both have an id
      return course1.id === course2.id;
    }

    // else compare by name
    return this.getCourseName(course1) === this.getCourseName(course2);
  }

  // Creates one or more repeat copies of the course (if it's marked as a ribbon course). The copies are added
  // to the plan in the corresponding periods marked in the ribbon field.
  planRibbonCourses(plan: PlanDetails, ribbonCourse: Course): void {
    const ribbonRepeatCourses: Course[] = this.createRibbonRepeatCourses(plan, ribbonCourse);
    ribbonRepeatCourses.forEach((ribbonRepeatCourse) => {
      const year = this.planHelper.getPlanYear(plan, ribbonRepeatCourse.yearNr);
      const period = this.planHelper.getPlanPeriod(year.periods, ribbonRepeatCourse.periodNr);
      period.courses.push(ribbonRepeatCourse);
      // Re-sort courses: ribbon courses first, then by code
      period.courses = this.sortCourses(period.courses);
      // re-apply course tests
      this.planHelper.refreshCourseTests(plan, period);
    });
  }

  getTestCount(course: Course | undefined, courseTestBlock: CourseTestBlock | undefined): number {
    if (course?.courseTests) {
      return course.courseTests.length;
    }
    if (courseTestBlock?.courseTests) {
      return courseTestBlock.courseTests.length;
    }
    return 0;
  }

  getCoursePoints(course: PossibleCustomCourse): number {
    return (this.isCustomCourse(course) ? course.customCoursePoints : course.studyPoints) ?? 0;
  }

  isRemovableFromPlanLater(course: Course): boolean {
    return (
      (this.authService.isRoleStudent() || this.authService.isRoleEmployeeDraftPlans()) &&
      !course.isEnrolled &&
      !this.isCustomCourse(course) &&
      !course.isCourseAtAnotherInstitution
    );
  }

  private createRibbonRepeatCourses(plan: PlanDetails, ribbonCourse: Course): Course[] {
    const ribbonRepeatCourses: Course[] = [];

    const countAcademicYears: number = plan.studyYears.length;
    const countPeriods: number = plan.studyYears[0].periods.length;
    const orgPeriodNr: number = (ribbonCourse.yearNr - 1) * countPeriods + ribbonCourse.periodNr; // grid number of the 'original' course
    const totalCountPeriods: number = countAcademicYears * countPeriods; // max number of the grid

    for (let i = 1; i < ribbonCourse.ribbonPeriods.length; i++) {
      const ribbonRepeatCourse = { ...ribbonCourse };
      const ribbonPeriodNr = orgPeriodNr + ribbonRepeatCourse.ribbonPeriods[i] - ribbonRepeatCourse.ribbonPeriods[0]; // lint periode nr
      const academicYear: number = this.getRibbonAcademicYear(ribbonPeriodNr, countPeriods); // get college year
      const periodNr: number = this.getRibbonPeriodNumber(ribbonPeriodNr, countPeriods); // get period number

      // if period count is bigger than ribbon period number, add a ribbon repeat course
      if (totalCountPeriods >= ribbonPeriodNr) {
        ribbonRepeatCourse.isRibbonRepeat = true;
        ribbonRepeatCourse.yearNr = academicYear;
        ribbonRepeatCourse.periodNr = periodNr;
        ribbonRepeatCourse.ribbonNr = ribbonRepeatCourse.ribbonNr + i;
        ribbonRepeatCourse.statusMessages = undefined;
        ribbonRepeatCourse.studyPoints = 0;
        ribbonRepeatCourses.push(ribbonRepeatCourse);
      }
    }

    return ribbonRepeatCourses;
  }

  private getRibbonAcademicYear(ribbonNr: number, periods: number): number {
    return Math.ceil(ribbonNr / periods);
  }

  private getRibbonPeriodNumber(ribbonNr: number, periods: number): number {
    return ((ribbonNr - 1) % periods) + 1;
  }

  public isCoursePlannable(course: Course): boolean {
    return !!(course?.yearNr === 0) && !this.isCustomCourse(course) && !course.isCourseAtAnotherInstitution;
  }

  public findPlannedCourse(course: PossibleCustomCourse, selectedPlan?: PlanDetails): Course | undefined {
    return selectedPlan?.studyYears
      .flatMap((year) => year.periods)
      .flatMap((period) => period.courses)
      .find((thisCourse) => this.isSameCourse(course, thisCourse) && thisCourse.yearNr !== undefined);
  }

  public isPlannedCourse(course: Course, selectedPlan?: PlanDetails): boolean {
    return this.findPlannedCourse(course, selectedPlan) !== undefined;
  }

  public getPlannedText(course: Course): string {
    if (course.yearNr > 0 && course.periodDescription !== "") {
      const plannedText = `${this.translate.instant("schedule.labelAcademicYear")} ${course.yearNr}`;

      if (this.hasRibbonPeriods(course)) {
        return plannedText;
      }

      return plannedText.concat(` - ${course.periodDescription}`);
    }

    return "";
  }

  public updateCoursePeriodInfo(course: Course, selectedPlan: PlanDetails | undefined): Course {
    if (!selectedPlan) {
      return course;
    }

    const planCourse = selectedPlan.studyYears
      .flatMap((year) => year.periods)
      .flatMap((period) => period.courses)
      .find((c) => this.isSameCourse(c, course));

    if (planCourse) {
      course.yearNr = planCourse.yearNr;
      course.periodNr = planCourse.periodNr;
      course.periodDescription = planCourse.periodDescription;
    }

    return course;
  }

  /*
   * Sort courses: ribbon courses first, then the other non-custom courses, then custom courses,
   * by code or name or custom course name.
   */
  public sortCourses(courses: Course[]): Course[] {
    return [
      ...courses
        .filter((c) => this.hasRibbonPeriods(c) && !this.isCustomCourse(c))
        .sort((a, b) => this.sortCourse(a, b)),
      ...courses
        .filter((c) => !this.hasRibbonPeriods(c) && !this.isCustomCourse(c))
        .sort((a, b) => this.sortCourse(a, b)),
      ...courses.filter((c) => this.isCustomCourse(c)).sort((a, b) => this.sortCourse(a, b)),
    ];
  }

  private readonly sortCourse = (course1: Course, course2: Course): number => {
    const code1 = course1.code ?? course1.name ?? course1.customCourseName ?? "";
    const code2 = course2.code ?? course2.name ?? course2.customCourseName ?? "";
    return code1.localeCompare(code2);
  };

  isMovableToPlanLater(course: Course, plan?: PlanDetails): boolean {
    return !this.isCustomCourse(course) && !course.isInPlanLater && !this.courseMustStayInPlan(course, plan);
  }

  getExamComponentDescription(course: Course): string {
    // Don't use ?? instead of || because the specialisation or minor name can be an empty string
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const specialisationOrMinorName = course.specialisationName || course.minorName;
    return specialisationOrMinorName
      ? `${course.examComponentName} (${specialisationOrMinorName})`
      : course.examComponentName;
  }

  isMultiPeriodPlanningAllowed(courseDetail: CourseDetail): boolean {
    return courseDetail.isFlexCourse && !courseDetail.hasOnlyTests;
  }
}
